import React, { useState } from 'react';
import MilestoneStartModal from '../components/MilestoneStartModal';
import { milestoneApi } from '../services/api';
import type { Contract, Milestone } from '../types';

/**
 * Whether a contract's stages can be started or have their start changed at
 * all: it is signed by both and has real stages. The timeline then decides per
 * stage (not finished, not replaced, not in conflict, who may change what).
 */
export const canManageMilestoneStarts = (contract: Contract, milestones: Milestone[]): boolean =>
  milestones.some(milestone => (milestone.itemType ?? 'milestone') === 'milestone') &&
  (contract.signingStatus === 'signed' || contract.draftStatus === 'signed_by_both');

interface UseMilestoneStartsOptions {
  side: 'company' | 'person';
  contracts: Contract[];
  milestonesByContract: Record<string, Milestone[]>;
  /** The contract's stages after a saved start, so the page updates in place. */
  onSaved: (contract: Contract, milestones: Milestone[]) => void;
}

/**
 * "Markera som startad" / "Ändra start" in a timeline's "Vad vill du göra?",
 * shared by the project page and the start pages' timelines so both offer the
 * same action under the same conditions (ClickUp 869fcmwaa).
 */
export function useMilestoneStarts({ side, contracts, milestonesByContract, onSaved }: UseMilestoneStartsOptions) {
  const [target, setTarget] = useState<{ contract: Contract; milestoneId: string } | null>(null);

  /** The props ContractTimeline needs to offer the start action. */
  const timelineStartProps = (contract: Contract, milestones: Milestone[]) => ({
    canManageStarts: canManageMilestoneStarts(contract, milestones),
    onManageStarts: (milestone: Milestone) => setTarget({
      contract: contracts.find(candidate => candidate.id === milestone.contractId) ?? contract,
      milestoneId: milestone.id
    })
  });

  const startModal = target ? (
    <MilestoneStartModal
      milestones={(milestonesByContract[target.contract.id] ?? []).filter(milestone => milestone.id === target.milestoneId)}
      userRole={side}
      projectId={target.contract.projectId}
      onSave={async (decisions, acceptFundingRisk) => {
        const updated = acceptFundingRisk
          ? await milestoneApi.updateStarts(target.contract.id, decisions, 'manual', true)
          : await milestoneApi.updateStarts(target.contract.id, decisions, 'manual');
        onSaved(target.contract, updated);
      }}
      onClose={() => setTarget(null)}
    />
  ) : null;

  return { timelineStartProps, startModal };
}
